import { Box, Divider, Flex } from '@televet/kibble-ui';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Checkbox } from '@televet/kibble-ui/build/components/Checkbox';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Menu, MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import React, { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import { IOption } from 'shared/components/Dropdown';
import { phoneValidationRegex } from 'shared/constants';
import { GraphQLErrorPolicies } from 'shared/enums/GraphQLErrorPolicies';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  TwilioVerificationStatus,
  useGetClinicSettingsGeneralQuery,
  useSubscribeToClinicSettingsGeneralTwilioVerificationSubscription,
  useUpdateClinicSettingsGeneralVerifyTwilioCallerIdMutation,
} from 'shared/types/graphql';
import { formatPhoneNumber, validatePhoneNumber } from 'shared/utils';

enum PhoneTreeChoices {
  Wait = 'Wait',
  Press = 'Press',
}

enum VerificationSteps {
  InformationCollection,
  CallVerifying,
  NumberPreviouslyVerified,
  CallSuccessfullyVerified,
  CallVerificationUnsuccessful,
}

interface Action {
  type: string;
  value: number;
  description: string;
}

/**
 * The user has two options to choose from: Wait, and Press. Both use numbers
 */
const phoneTreeOptions: MenuItemProps[] = [
  { label: PhoneTreeChoices.Wait, value: 'second(s)' },
  { label: PhoneTreeChoices.Press, value: 'key' },
];

type VerifyPhoneNumberModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const VerifyPhoneNumberModal = ({ isOpen, onClose }: VerifyPhoneNumberModalProps): JSX.Element => {
  const [phoneTreeSelections, setPhoneTreeSelections] = useState<Action[]>([]);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [isCallerId, setIsCallerId] = useState(true);
  const [currentVerificationStep, setCurrentVerificationStep] = useState<VerificationSteps>(
    VerificationSteps.InformationCollection,
  );
  const [callerIdVerificationCode, setCallerIdVerificationCode] = useState('');
  const toast = useToast();
  const { currentClinicId } = useClinicUser();
  const [twilioCallSid, setTwilioCallSid] = useState<string>();
  const [verificationError, setVerificationError] = useState<string>('');
  const [displayPhoneValidation, setDisplayPhoneValidation] = useState(false);

  const { data: currentClinic, refetch: refetchCurrentClinicData } = useGetClinicSettingsGeneralQuery({
    variables: { id: currentClinicId || '' },
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
  });

  useSubscribeToClinicSettingsGeneralTwilioVerificationSubscription({
    variables: { where: { node: { twilioCallSid: { equals: twilioCallSid } } } },
    skip: !twilioCallSid || currentVerificationStep === VerificationSteps.CallSuccessfullyVerified,
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    onData: ({ data }) => {
      if (data?.data?.twilioVerificationChanged?.node?.status === TwilioVerificationStatus.Success) {
        setCurrentVerificationStep(VerificationSteps.CallSuccessfullyVerified);
      }
    },
  });

  const [twilioVerifyCallerId] = useUpdateClinicSettingsGeneralVerifyTwilioCallerIdMutation({
    errorPolicy: GraphQLErrorPolicies.All,
  });

  const isPhoneNumberValid = useMemo(() => validatePhoneNumber(phoneNumber), [phoneNumber]);

  const addNewPhoneTreeStep = (): void => {
    setPhoneTreeSelections((currentPhoneTreeSteps) => [
      ...currentPhoneTreeSteps,
      {
        type: PhoneTreeChoices.Wait,
        value: 5,
        description: 'second(s)',
      },
    ]);
  };

  useEffect(() => {
    return (): void => {
      setCurrentVerificationStep(VerificationSteps.InformationCollection);
      setVerificationError('');
    };
  }, []);

  const getExtension = useCallback((): string => {
    return phoneTreeSelections
      .map((action: Action) => {
        if (action.type === PhoneTreeChoices.Wait) {
          return 'w'.repeat(action.value * 2);
        } else if (action.type === PhoneTreeChoices.Press) {
          return action.value;
        }
        return '';
      })
      .join('');
  }, [phoneTreeSelections]);

  const editPhoneTreeStep = (option: IOption, index: number, value: number): void => {
    setPhoneTreeSelections((currentPhoneTreeSteps) => {
      const copy: Action[] = [...currentPhoneTreeSteps];
      copy[index] = {
        type: option.text,
        description: option.value,
        value,
      };
      return copy;
    });
  };

  const removeTreeSelection = (_index: number): void => {
    setPhoneTreeSelections((currentPhoneTreeSteps) => currentPhoneTreeSteps.filter((step, index) => index !== _index));
  };

  const setSelectionValue = (value: string, index: number): void => {
    setPhoneTreeSelections((currentPhoneTreeSteps) => {
      const copy: Action[] = [...currentPhoneTreeSteps];
      copy[index] = {
        type: copy[index].type,
        description: copy[index].description,
        value: Number(value) > 9 ? 9 : Number(value) < 0 ? 0 : Number(value),
      };
      return copy;
    });
  };

  const verifyPhoneNumber = useCallback(async (): Promise<void> => {
    if (!isPhoneNumberValid) {
      toast({
        title: 'Please provide a valid phone number',
        status: 'error',
      });
      setCurrentVerificationStep(VerificationSteps.CallVerificationUnsuccessful);
      setVerificationError('The phone number you provided was invalid.');
      return;
    }

    setCurrentVerificationStep(VerificationSteps.CallVerifying);

    try {
      const { data, errors } = await twilioVerifyCallerId({
        variables: {
          data: {
            phoneNumber,
            friendlyName: currentClinic?.findUniqueClinic?.name || '',
            extension: getExtension(),
            callDelay: 0,
            isCallerId,
          },
        },
      });

      if (data?.twilioVerifyCallerId) {
        setCallerIdVerificationCode(data?.twilioVerifyCallerId.validationCode);
        setTwilioCallSid(data?.twilioVerifyCallerId.callSid);
      } else if (errors) {
        if (errors[0]?.extensions?.exception?.code === 21450) {
          setCurrentVerificationStep(VerificationSteps.NumberPreviouslyVerified);
        } else {
          setCurrentVerificationStep(VerificationSteps.CallVerificationUnsuccessful);
          if (errors[0].message.startsWith('phone_number is not a valid phone number:')) {
            // Making the twilio error message more user friendly
            setVerificationError(`The number ${phoneNumber} is not a valid phone number.`);
          } else {
            setVerificationError(errors[0].message);
          }
        }

        console.error(errors[0]);
      }
    } catch (e) {
      console.log(e);
    }
  }, [
    isPhoneNumberValid,
    toast,
    twilioVerifyCallerId,
    phoneNumber,
    currentClinic?.findUniqueClinic?.name,
    getExtension,
    isCallerId,
  ]);

  const resetModal = useCallback((): void => {
    setPhoneNumber('');
    setIsCallerId(true);
    setVerificationError('');
    setCurrentVerificationStep(VerificationSteps.InformationCollection);
    setDisplayPhoneValidation(false);
  }, []);

  const closeVerification = useCallback((): void => {
    resetModal();
    onClose();
    refetchCurrentClinicData();
  }, [resetModal, onClose, refetchCurrentClinicData]);

  return (
    <Modal size="md" isOpen={isOpen} onClose={closeVerification} scrollBehavior="inside">
      <ModalHeader data-testid="verification-title">Verify Clinic Phone Number</ModalHeader>
      <ModalCloseButton data-testid="close-modal-button" />
      <ModalBody>
        <Flex flexDirection="column" gap={4}>
          {currentVerificationStep === VerificationSteps.InformationCollection ? (
            <>
              <Text as="p" mt={2} mb={0}>
                Reach clients from Otto with a familiar phone number. Enter your clinic’s number below to receive an
                automated call with a confirmation code.
              </Text>
              <TextInput
                placeholder="Enter Phone Number"
                autoFocus={true}
                value={phoneNumber}
                data-testid="phone-number-verification-input"
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  if (
                    (e.target.value.match(phoneValidationRegex) && e.target.value.length <= 10) ||
                    !e.target.value.length
                  )
                    setPhoneNumber(e.target.value);
                }}
                isInvalid={displayPhoneValidation && !isPhoneNumberValid}
                onBlur={(): void => {
                  setDisplayPhoneValidation(true);
                }}
                errorText="Please enter a valid phone number"
              />
              <Box>
                {/* The surrounding Box ensures proper spacing and alignment for the Checkbox component */}
                <Checkbox
                  name="isCallerId"
                  isChecked={isCallerId}
                  onChange={({ target: { checked } }): void => {
                    setIsCallerId(checked);
                  }}
                >
                  Use this number for outgoing phone calls from Flow
                </Checkbox>
              </Box>
              <Divider />
              <Heading color="text.subtle" data-testid="verification-phone-tree-message" size="xs" textAlign="left">
                Have an extension or phone tree?
              </Heading>
              <Text>Add automated key presses and wait times below to navigate your phone system.</Text>
              <Flex direction="column" gap={2}>
                {phoneTreeSelections.map((selection, index) => {
                  return (
                    <Flex key={index} gap={2} alignItems="center">
                      <Menu
                        buttonProps={
                          {
                            variant: 'secondarySubtle',
                            width: '90px',
                            minWidth: '90px',
                            text: selection.type,
                            rightIconName: 'chevronDown',
                            'data-mixpanel-name': 'Edit phone tree step type',
                            'data-testid': `edit-phone-tree-step-type-${index}`,
                          } as ComponentProps<typeof Menu>['buttonProps']
                        }
                        listProps={{
                          isSearchable: false,
                          isMultiSelect: false,
                          canSelectAll: false,
                          canSelectGroups: false,
                          onSelect: (selectedItem): void => {
                            editPhoneTreeStep(
                              {
                                text: selectedItem.label?.toString() || '',
                                value: selectedItem.value,
                              },
                              index,
                              selection.value,
                            );
                          },
                          menuItems: phoneTreeOptions,
                        }}
                      />
                      <Box>
                        <TextInput
                          value={Number(selection.value)}
                          type="number"
                          data-testid={`edit-phone-tree-step-value-${index}`}
                          onChange={(e): void => setSelectionValue(e.target.value, index)}
                        />
                      </Box>
                      <Box width="80px">{selection.description}</Box>
                      <Button
                        variant="ghostNeutral"
                        data-testid={`remove-phone-tree-step-${index}`}
                        onClick={(): void => removeTreeSelection(index)}
                        iconName="close"
                        iconPosition="right"
                        size="sm"
                      ></Button>
                    </Flex>
                  );
                })}
              </Flex>

              <ModalFooter alignItems="stretch" flexDirection="column" px={0} pt={0} pb={4} gap={2}>
                <Button
                  variant="secondary"
                  data-testid="add-new-phone-tree-step-button"
                  type="button"
                  onClick={addNewPhoneTreeStep}
                >
                  Add a Step
                </Button>
                <Tooltip
                  label={isPhoneNumberValid ? '' : 'Please enter a valid phone number'}
                  zIndex={999999}
                  placement="auto"
                >
                  {/* If the button is disabled, the tooltip won't show, we need to wrap it around this box */}
                  <Box w="100%">
                    <Button
                      disabled={!isPhoneNumberValid}
                      onClick={verifyPhoneNumber}
                      data-testid="submit-verify-phone-number-button"
                      data-mixpanel-name="Ready for call"
                      isFullWidth={true}
                    >
                      Ready for Call
                    </Button>
                  </Box>
                </Tooltip>
              </ModalFooter>
            </>
          ) : (
            <Box my={3}>
              {currentVerificationStep === VerificationSteps.CallVerifying && (
                <>
                  {callerIdVerificationCode ? (
                    <Flex flexDirection="column" gap={3}>
                      <Flex
                        backgroundColor="background.lessSubtle"
                        p={3}
                        borderRadius="sm"
                        width="auto"
                        alignSelf="center"
                      >
                        <Text size="lg">{callerIdVerificationCode}</Text>
                      </Flex>
                      <Text size="sm" color="text.subtle">
                        Calling {formatPhoneNumber(phoneNumber)}. Please answer the automated call and enter the 6-digit
                        validation code shown above
                      </Text>
                      <Flex flexDirection="column" alignItems="center" gap={3}>
                        <Spinner />
                      </Flex>
                    </Flex>
                  ) : (
                    <Spinner />
                  )}
                </>
              )}
              {currentVerificationStep === VerificationSteps.NumberPreviouslyVerified && (
                <Box>
                  <Text as="p" mb={3}>
                    That phone number has already been verified and is ready to use in Flow.
                  </Text>
                  <Button data-testid="okay-button" onClick={(): void => closeVerification()} isFullWidth={true}>
                    Okay
                  </Button>
                </Box>
              )}
              {currentVerificationStep === VerificationSteps.CallVerificationUnsuccessful && (
                <Box>
                  <Text as="p" mb={3}>
                    {verificationError}
                  </Text>
                  <Button data-testid="retry-verification-button" onClick={(): void => resetModal()} isFullWidth={true}>
                    Try Again
                  </Button>
                </Box>
              )}
              {currentVerificationStep === VerificationSteps.CallSuccessfullyVerified && (
                <Box>
                  <Text as="p" mb={3}>
                    {formatPhoneNumber(phoneNumber)} has been successfully verified!
                    {isCallerId ? ' Phone calls from Flow will now use this phone number.' : ''}
                  </Text>
                  <Button data-testid="okay-button" onClick={(): void => closeVerification()} isFullWidth={true}>
                    Okay
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Flex>
      </ModalBody>
    </Modal>
  );
};

export default VerifyPhoneNumberModal;
